import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { DataTable, type Column } from '../../components/ui/data-table'
import { toast } from 'sonner'
import { getPayouts } from '../../services/payout'
import { formatTimestamp } from '../../utils/format'
import type { Payout, Pagination } from '../../types'

const statusLabels: Record<string, string> = {
  PENDING: '待審核',
  APPROVED: '已審批',
  PROCESSING: '處理中',
  COMPLETED: '已完成',
  REJECTED: '已拒絕',
  FAILED: '失敗',
}

const formatProcessingDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('zh-CN', {
    timeZone: 'Asia/Shanghai',
  })

export default function PayoutList() {
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  const fetchPayouts = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getPayouts({ page, size })
      .then((res) => {
        setPayouts(res.data.items)
        setPagination(res.data.pagination)
      })
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error ? error.message : '結款記錄載入失敗',
        ),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchPayouts()
  }, [])

  const formatAmount = (amount: string | number) =>
    Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

  const handleCreatePayout = () => {
    navigate('/dashboard/payouts/new')
  }

  const columns: Column<Payout>[] = [
    { key: 'id', header: '結款 ID', render: (r) => r.id },
    {
      key: 'amount',
      header: '金額',
      className: 'text-right tabular-nums',
      render: (r) => `$${formatAmount(r.amount)} ${r.currency}`,
    },
    { key: 'fee', header: '手續費', className: 'text-right tabular-nums', render: (r) => `$${formatAmount(r.fee)}` },
    {
      key: 'netAmount',
      header: '實際到賬',
      className: 'text-right tabular-nums',
      render: (r) => `$${formatAmount(r.netAmount)}`,
    },
    {
      key: 'method',
      header: '方式',
      render: (r) => (
        <div>
          {r.method}
          {r.usdtServiceType && (
            <p className="text-primary">
              {r.usdtServiceType === 'URGENT' ? '緊急提現' : '普通提現'}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'expected',
      header: '預計處理／到賬',
      className: 'text-xs text-muted-foreground',
      render: (r) =>
        r.expectedProcessingAt
          ? r.usdtServiceType === 'URGENT'
            ? new Date(r.expectedProcessingAt).toLocaleString('zh-CN', {
                timeZone: 'Asia/Hong_Kong',
                hour12: false,
              })
            : formatProcessingDate(r.expectedProcessingAt)
          : '-',
    },
    {
      key: 'status',
      header: '狀態',
      render: (r) => (
        <span className="rounded-md bg-muted px-2 py-1">
          {statusLabels[r.status] || r.status}
        </span>
      ),
    },
    {
      key: 'created',
      header: '創建時間',
      className: 'text-xs text-muted-foreground',
      render: (r) => formatTimestamp(r.createdAt),
    },
    {
      key: 'details',
      header: '費用明細',
      className: 'whitespace-nowrap',
      render: (r) =>
        r.usdtServiceType != null && r.payoutFeeFixed != null ? (
          <details>
            <summary className="cursor-pointer text-primary">查看明細</summary>
            <dl className="min-w-48 space-y-1 pt-2">
              <dt>提交時費率</dt>
              <dd>
                ${formatAmount(r.payoutFeeFixed || 0)} +{' '}
                {(Number(r.payoutFeeRate || 0) * 100).toFixed(2)}%
              </dd>
              <dt>比例手續費</dt>
              <dd>${formatAmount(r.payoutPercentageFee || 0)}</dd>
              <dt>網絡費</dt>
              <dd>${formatAmount(r.payoutNetworkFee || 0)}</dd>
              <dt>手續費合計</dt>
              <dd>${formatAmount(r.fee)}</dd>
            </dl>
          </details>
        ) : (
          '-'
        ),
    },
  ]
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">結款記錄</h1>
        <Button onClick={handleCreatePayout}>發起結款</Button>
      </div>
      <Card>
        <DataTable
          columns={columns}
          data={payouts}
          rowKey="id"
          loading={loading}
          pagination={{
            page: pagination.page,
            size: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchPayouts(1, size)
              } else fetchPayouts(page, size)
            },
          }}
        />
      </Card>
    </div>
  )
}
